import {
    AlertCircle,
    Calendar,
    CheckCircle,
    Clock,
    Edit,
    Eye,
    MapPin,
    Pause,
    Phone,
    Play,
    Plus,
    RefreshCw,
    Trash2,
    User,
    XCircle
} from "lucide-react";
import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosHandler";

const DrivingSchoolManagement = () => {
    const [activeTab, setActiveTab] = useState("schools");
    const [schools, setSchools] = useState([]);
    const [requests, setRequests] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState("add");
    const [selectedItem, setSelectedItem] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        userName: "",
        password: "",
        icbcPassword: "",
        certificationNumber: "",
        instructorLicense: "",
        schoolPhone: "",
        schoolName: "",
    });

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        try {
            if (activeTab === "schools") {
                const response = await axios.get("/api/driving-schools/schools");
                setSchools(response.data.data);
            } else if (activeTab === "requests") {
                const response = await axios.get("/api/driving-schools/requests");
                setRequests(response.data.data);
            } else if (activeTab === "appointments") {
                const response = await axios.get("/api/driving-schools/appointments");
                setAppointments(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (modalType === "add") {
                await axios.post("/api/driving-schools/schools", formData);
            } else {
                await axios.put(`/api/driving-schools/schools/${selectedItem._id}`, formData);
            }
            setShowModal(false);
            resetForm();
            fetchData();
        } catch (error) {
            console.error("Error saving driving school:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (school) => {
        setSelectedItem(school);
        setModalType("edit");
        setFormData({
            name: school.name,
            userName: school.userName,
            password: "",
            icbcPassword: school.icbcPassword || "",
            certificationNumber: school.certificationNumber,
            instructorLicense: school.instructorLicense,
            schoolPhone: school.schoolPhone,
            schoolName: school.schoolName,
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this driving school?")) {
            try {
                await axios.delete(`/api/driving-schools/schools/${id}`);
                fetchData();
            } catch (error) {
                console.error("Error deleting driving school:", error);
            }
        }
    };

    const handleTestLogin = async (schoolId) => {
        try {
            setLoading(true);
            const response = await axios.post(`/api/driving-schools/test-login/${schoolId}`);
            alert(`Login test successful: ${response.data.message}`);
        } catch (error) {
            const message = error.response?.data?.message || "Login test failed";
            alert(`Login test failed: ${message}`);
            console.error("Error testing login:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleStartBooking = async (requestId) => {
        try {
            await axios.post(`/api/driving-schools/requests/${requestId}/start-booking`);
            fetchData();
        } catch (error) {
            console.error("Error starting booking:", error);
        }
    };

    const handleCancelRequest = async (requestId) => {
        if (window.confirm("Are you sure you want to cancel this request?")) {
            try {
                await axios.post(`/api/driving-schools/requests/${requestId}/cancel`);
                fetchData();
            } catch (error) {
                console.error("Error cancelling request:", error);
            }
        }
    };

    const handlePauseRequest = async (requestId) => {
        try {
            await axios.put(`/api/driving-schools/requests/${requestId}`, { status: 'pending' });
            fetchData();
        } catch (error) {
            console.error("Error pausing request:", error);
        }
    };

    const resetForm = () => {
        setFormData({
            name: "",
            userName: "",
            password: "",
            icbcPassword: "",
            certificationNumber: "",
            instructorLicense: "",
            schoolPhone: "",
            schoolName: "",
        });
        setSelectedItem(null);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "pending":
                return "bg-yellow-100 text-yellow-800";
            case "searching":
                return "bg-blue-100 text-blue-800";
            case "appointment_found":
                return "bg-green-100 text-green-800";
            case "booked":
                return "bg-green-100 text-green-800";
            case "cancelled":
                return "bg-red-100 text-red-800";
            case "completed":
                return "bg-gray-100 text-gray-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "pending":
                return <Clock className="w-4 h-4" />;
            case "searching":
                return <RefreshCw className="w-4 h-4 animate-spin" />;
            case "appointment_found":
            case "booked":
                return <CheckCircle className="w-4 h-4" />;
            case "cancelled":
                return <XCircle className="w-4 h-4" />;
            case "completed":
                return <CheckCircle className="w-4 h-4" />;
            default:
                return <AlertCircle className="w-4 h-4" />;
        }
    };

    const renderSchoolsTab = () => (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Driving Schools</h2>
                <button
                    onClick={() => {
                        setModalType("add");
                        setShowModal(true);
                        resetForm();
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                >
                    <Plus className="w-4 h-4" />
                    Add School
                </button>
            </div>

            {loading ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
            ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {schools.map((school) => (
                        <div key={school._id} className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-lg font-semibold">{school.name}</h3>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleTestLogin(school._id)}
                                        className="text-green-600 hover:text-green-800"
                                        title="Test ICBC Login"
                                    >
                                        <CheckCircle className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleEdit(school)}
                                        className="text-blue-600 hover:text-blue-800"
                                        title="Edit School"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(school._id)}
                                        className="text-red-600 hover:text-red-800"
                                        title="Delete School"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2 text-sm text-gray-600">
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    <span>{school.userName}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    <span>{school.schoolName}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Phone className="w-4 h-4" />
                                    <span>{school.schoolPhone}</span>
                                </div>
                            </div>

                            <div className="mt-4">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${school.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                    }`}>
                                    {school.isActive ? "Active" : "Inactive"}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderRequestsTab = () => (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Road Test Requests</h2>
            </div>

            {loading ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {/* Table view - large screens */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Request #
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Customer
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        School
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date Range
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {requests.map((request) => (
                                    <tr key={request._id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {request.requestNumber}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <div>
                                                <div className="font-medium">
                                                    {request.customerInfo.firstName} {request.customerInfo.lastName}
                                                </div>
                                                <div className="text-gray-500">{request.customerInfo.phone}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {request.drivingSchool?.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <div>
                                                <div>{new Date(request.startDate).toLocaleDateString()}</div>
                                                <div className="text-gray-500">to {new Date(request.endDate).toLocaleDateString()}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                                {getStatusIcon(request.status)}
                                                <span className="ml-1">{request.status.replace('_', ' ')}</span>
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex gap-2">
                                                {/* Start/Pause toggle */}
                                                {request.status !== "booked" && request.status !== "cancelled" && (
                                                    request.status === "searching" ? (
                                                        <button
                                                            onClick={() => handlePauseRequest(request._id)}
                                                            className="text-yellow-600 hover:text-yellow-800 flex items-center gap-1"
                                                        >
                                                            <Pause className="w-4 h-4" />
                                                            Pause
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleStartBooking(request._id)}
                                                            className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                                                        >
                                                            <Play className="w-4 h-4" />
                                                            Start
                                                        </button>
                                                    )
                                                )}

                                                {/* Cancel button */}
                                                {request.status !== "cancelled" && request.status !== "booked" && (
                                                    <button
                                                        onClick={() => handleCancelRequest(request._id)}
                                                        className="text-red-600 hover:text-red-900 flex items-center gap-1"
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                        Cancel
                                                    </button>
                                                )}

                                                {/* See (visible) for admins */}
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            await axios.post(`/api/driving-schools/requests/${request._id}/start-booking`, { showBrowser: true, slowMo: 300 });
                                                            alert('Visible booking started. A browser window will open on the server.');
                                                        } catch (e) {
                                                            alert('Failed to start visible booking');
                                                        }
                                                    }}
                                                    className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
                                                    title="See live booking"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                    See
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setSelectedItem(request);
                                                        setModalType("view");
                                                        setShowModal(true);
                                                    }}
                                                    className="text-gray-600 hover:text-gray-900"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Card view - mobile & tablets */}
                    <div className="lg:hidden p-4 space-y-4">
                        {requests.map((request) => (
                            <div key={request._id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <div>
                                        <div className="text-sm text-gray-500">Request #</div>
                                        <div className="text-base font-semibold text-gray-900">{request.requestNumber}</div>
                                    </div>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                        {getStatusIcon(request.status)}
                                        <span className="ml-1">{request.status.replace('_', ' ')}</span>
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-sm text-gray-700 mb-3">
                                    <div>
                                        <div className="text-xs text-gray-500">Customer</div>
                                        <div className="font-medium">{request.customerInfo.firstName} {request.customerInfo.lastName}</div>
                                        <div className="text-gray-500">{request.customerInfo.phone}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500">School</div>
                                        <div>{request.drivingSchool?.name || '-'}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500">Start</div>
                                        <div>{new Date(request.startDate).toLocaleDateString()}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500">End</div>
                                        <div>{new Date(request.endDate).toLocaleDateString()}</div>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                                    {request.status !== "booked" && request.status !== "cancelled" && (
                                        request.status === "searching" ? (
                                            <button
                                                onClick={() => handlePauseRequest(request._id)}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200"
                                            >
                                                <Pause className="w-4 h-4" />
                                                Pause
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleStartBooking(request._id)}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                            >
                                                <Play className="w-4 h-4" />
                                                Start
                                            </button>
                                        )
                                    )}

                                    {request.status !== "cancelled" && request.status !== "booked" && (
                                        <button
                                            onClick={() => handleCancelRequest(request._id)}
                                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-white bg-red-600 hover:bg-red-700"
                                        >
                                            <XCircle className="w-4 h-4" />
                                            Cancel
                                        </button>
                                    )}

                                    <button
                                        onClick={async () => {
                                            try {
                                                await axios.post(`/api/driving-schools/requests/${request._id}/start-booking`, { showBrowser: true, slowMo: 300 });
                                                alert('Visible booking started. A browser window will open on the server.');
                                            } catch (e) {
                                                alert('Failed to start visible booking');
                                            }
                                        }}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200"
                                        title="See live booking"
                                    >
                                        <Eye className="w-4 h-4" />
                                        See
                                    </button>

                                    <button
                                        onClick={() => {
                                            setSelectedItem(request);
                                            setModalType("view");
                                            setShowModal(true);
                                        }}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200"
                                    >
                                        <Eye className="w-4 h-4" />
                                        Details
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const renderAppointmentsTab = () => (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Road Test Appointments</h2>
            </div>

            {loading ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
            ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {appointments.map((appointment) => (
                        <div key={appointment._id} className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-lg font-semibold">{appointment.appointmentNumber}</h3>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                                    {appointment.status}
                                </span>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm">
                                    <User className="w-4 h-4 text-gray-500" />
                                    <span>{appointment.customerInfo.firstName} {appointment.customerInfo.lastName}</span>
                                </div>

                                <div className="flex items-center gap-2 text-sm">
                                    <Calendar className="w-4 h-4 text-gray-500" />
                                    <span>{new Date(appointment.date).toLocaleDateString()}</span>
                                </div>

                                <div className="flex items-center gap-2 text-sm">
                                    <Clock className="w-4 h-4 text-gray-500" />
                                    <span>{appointment.time}</span>
                                </div>

                                <div className="flex items-center gap-2 text-sm">
                                    <MapPin className="w-4 h-4 text-gray-500" />
                                    <span>{appointment.office}</span>
                                </div>

                                {appointment.confirmationNumber && (
                                    <div className="text-sm text-gray-600">
                                        Confirmation: {appointment.confirmationNumber}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Driving School Management</h1>
                <p className="text-gray-600">Manage driving schools, road test requests, and appointments</p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    {[
                        { id: "schools", label: "Driving Schools" },
                        { id: "requests", label: "Road Test Requests" },
                        { id: "appointments", label: "Appointments" },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                ? "border-blue-500 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === "schools" && renderSchoolsTab()}
            {activeTab === "requests" && renderRequestsTab()}
            {activeTab === "appointments" && renderAppointmentsTab()}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">
                                {modalType === "add" ? "Add Driving School" :
                                    modalType === "edit" ? "Edit Driving School" : "Request Details"}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <XCircle className="w-6 h-6" />
                            </button>
                        </div>

                        {modalType === "view" ? (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Request Number</label>
                                    <p className="mt-1 text-sm text-gray-900">{selectedItem?.requestNumber}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Customer</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {selectedItem?.customerInfo.firstName} {selectedItem?.customerInfo.lastName}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                                    <p className="mt-1 text-sm text-gray-900">{selectedItem?.customerInfo.phone}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">License Number</label>
                                    <p className="mt-1 text-sm text-gray-900">{selectedItem?.customerInfo.licenseNumber}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Date Range</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {new Date(selectedItem?.startDate).toLocaleDateString()} - {new Date(selectedItem?.endDate).toLocaleDateString()}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Status</label>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedItem?.status)}`}>
                                        {getStatusIcon(selectedItem?.status)}
                                        <span className="ml-1">{selectedItem?.status?.replace('_', ' ')}</span>
                                    </span>
                                </div>
                                {selectedItem?.notes && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedItem.notes}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">School Name</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">ICBC Username</label>
                                    <input
                                        type="text"
                                        value={formData.userName}
                                        onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">ICBC Password (Plain Text)</label>
                                    <input
                                        type="password"
                                        value={formData.icbcPassword}
                                        onChange={(e) => setFormData({ ...formData, icbcPassword: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        required
                                        placeholder="Enter the actual ICBC password (not encrypted)"
                                    />
                                    <p className="mt-1 text-sm text-gray-500">
                                        This is the actual password used to log into ICBC. It will be stored in plain text for automation purposes.
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {modalType === "edit" ? "New Password (leave blank to keep current)" : "Account Password"}
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        required={modalType === "add"}
                                        placeholder="Enter a password for this account (will be encrypted)"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Certification Number</label>
                                    <input
                                        type="text"
                                        value={formData.certificationNumber}
                                        onChange={(e) => setFormData({ ...formData, certificationNumber: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Instructor License</label>
                                    <input
                                        type="text"
                                        value={formData.instructorLicense}
                                        onChange={(e) => setFormData({ ...formData, instructorLicense: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">School Phone</label>
                                    <input
                                        type="text"
                                        value={formData.schoolPhone}
                                        onChange={(e) => setFormData({ ...formData, schoolPhone: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">School Name (for ICBC)</label>
                                    <input
                                        type="text"
                                        value={formData.schoolName}
                                        onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowModal(false)}
                                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {loading ? "Saving..." : modalType === "add" ? "Add School" : "Update School"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DrivingSchoolManagement; 